<template>
  <v-container
    id="eligibilitySummary"
  >
    <v-progress-linear
      v-show="loading"
      indeterminate
      color="green"
    />
    <div>
      <div class="oe-summary-title">
        <v-btn
          class="mx-2"
          fab
          dark
          x-small
          :color="approvals.color"
          @click="displaySection = !displaySection"
        >
          <v-icon
            v-show="!displaySection"
            dark
          >
            mdi-arrow-expand-vertical
          </v-icon>
          <v-icon
            v-show="displaySection"
            dark
          >
            mdi-arrow-expand-horizontal
          </v-icon>
        </v-btn>
        Eligibility Summary
      </div>
      <v-fab-transition>
        <note
          v-show="displaySection"
          :open-enrollment="openEnrollment"
          :section="sections.eligibility"
          summary="true"
        />
      </v-fab-transition>
      <div
        v-if="!loading && eligibilities && eligibilities.length"
      >
        <div
          v-for="(eligibility, i) in eligibilities"
          :key="i"
        >
          <v-fab-transition>
            <div
              v-show="displaySection"
              class="oe-summary-subsection mt-6"
            >
              <div class="text-center display-1">
                {{ eligibility.eligibility }}
              </div>
              <v-row>
                <v-col cols="6">
                  <base-display-value
                    v-model="eligibility.product"
                    label="Product"
                  />
                </v-col>
                <v-col cols="6">
                  <base-display-value
                    v-model="eligibility.carrier"
                    label="Carrier"
                  />
                </v-col>
              </v-row>
              <v-row
                class="eligibility-summary-group-summary"
              >
                <v-col
                  cols="12"
                  md="6"
                  class="eligibility-summary-group-name"
                >
                  <base-display-value
                    v-model="eligibility.eligibilityGroup"
                    label="Group"
                  />
                </v-col>
                <v-col
                  cols="12"
                  md="6"
                  class="eligibility-summary-group-details"
                >
                  <span
                    class="display-value-label font-weight-medium text--primary"
                  >
                    Group Details
                  </span>
                  <v-progress-linear
                    v-show="loadingEligibilityGroups"
                    indeterminate
                    color="green"
                  />
                  <div
                    v-if="!loadingEligibilityGroups"
                  >
                    <ul
                      v-for="(detail, j) in getGroupDetails(eligibility.oeEligibilityGroupId)"
                      :key="j"
                    >
                      <li>
                        {{ detail.locationName }} - {{ detail.jobClassName }}
                      </li>
                    </ul>
                  </div>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="6">
                  <base-display-value
                    v-model="eligibility.retroactiveEnrollmentsAllowed"
                    label="Retroactive Enrollments Allowed"
                    data-type="boolean"
                  />
                </v-col>
                <v-col cols="6">
                  <base-display-value
                    v-model="eligibility.eligibleForCoverage"
                    label="Domestic Partners Allowed"
                    data-type="boolean"
                  />
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="6">
                  <base-display-value
                    v-model="eligibility.cobraEligible"
                    label="COBRA Eligible"
                    data-type="boolean"
                  />
                </v-col>
                <v-col cols="6">
                  <base-display-value
                    v-model="eligibility.minHours"
                    label="Min Hours"
                  />
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="6">
                  <base-display-value
                    v-model="eligibility.waitingPeriod"
                    label="Waiting Period"
                  />
                </v-col>
                <v-col cols="6">
                  <base-display-value
                    v-model="eligibility.gracePeriod"
                    label="Grace Period"
                  />
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="6">
                  <base-display-value
                    v-model="eligibility.effectiveMode"
                    label="Effective Mode"
                  />
                </v-col>
                <v-col cols="6">
                  <base-display-date
                    v-model="eligibility.effectiveDate"
                    label="Effective Date"
                  />
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="6">
                  <base-display-value
                    v-model="eligibility.termMode"
                    label="Term Mode"
                  />
                </v-col>
                <v-col cols="6">
                  <base-display-value
                    v-model="eligibility.taxStatus"
                    label="Tax Status"
                  />
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="6">
                  <base-display-value
                    v-model="eligibility.newHiresCanEnrollThroughoutYear"
                    label="New Hires Can Enroll Throughout Year"
                    data-type="boolean"
                  />
                </v-col>
                <v-col cols="6">
                  <base-display-value
                    v-model="eligibility.requireDepSSN"
                    label="Require Dep SSN"
                    data-type="boolean"
                  />
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12">
                  <base-display-value
                    v-model="eligibility.payrollDeductionMode"
                    label="Payroll Deduction Mode"
                  />
                </v-col>
                <v-col cols="12">
                  <base-display-value
                    v-model="eligibility.employeeFirstDeductionMode"
                    label="Employee Deduction Mode"
                  />
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12">
                  <base-display-value
                    v-model="eligibility.bill"
                    label="Bill"
                  />
                </v-col>
              </v-row>
            </div>
          </v-fab-transition>
        </div>
      </div>
      <v-fab-transition>
        <v-alert
          v-show="displaySection && (!loading && (!eligibilities || !eligibilities.length))"
          type="info"
        >
          No eligibility details found
        </v-alert>
      </v-fab-transition>
      <v-fab-transition>
        <approval
          v-show="displaySection"
          :approvals="approvals"
          :allow-change="!print"
          @approval:changed="$emit('approval:changed')"
        />
      </v-fab-transition>
    </div>
    <base-material-snackbar
      v-model="displayError"
      type="error"
      v-bind="{
        top: true,
        right: true
      }"
    >
      {{ errorMessage }}
    </base-material-snackbar>
  </v-container>
</template>

<script>
  import TroodyApi from '@/services/troody/TroodyApi'
  import Approvals from '@/services/troody/Approvals'

  export default {
    name: 'EligibilitySummary',

    components: {
      Note: () => import('./Note'),
      Approval: () => import('./Approval'),
    },

    props: {
      expanded: {
        type: Boolean,
        default: false,
      },
      openEnrollment: {
        type: Object,
        default: null,
      },
      print: {
        type: Boolean,
        default: false,
      },
    },

    data: () => ({
      displaySection: true,

      loadingEligibility: false,
      loadingEligibilityGroups: false,

      errorMessage: null,
      displayError: false,
      formErrors: {},

      eligibilities: null,
      eligibilityGroups: null,

      eligibilityResource: new TroodyApi('oeEligibility/'),
      eligibilityGroupDetailResource: new TroodyApi('oeEligibilityGroupDetail/'),

      approvals: null,
    }),

    computed: {
      loading () {
        return (this.loadingEligibility)
      },

      sections () {
        return Approvals.sections
      },
    },

    watch: {
      expanded (val) {
        this.displaySection = val
      },
    },

    created () {
      this.initialize()
    },

    methods: {
      initialize () {
        this.formErrors = {}
        this.loadEligibility()
        this.loadEligibilityGroups()
        this.approvals = new Approvals(this.openEnrollment.id, Approvals.sections.eligibility.id)
        this.displaySection = this.expanded
      },

      sortEligibilities (data) {
        var sorted = data

        if (data && data.length) {
          data.sort((a, b) => {
            return a.orderNo - b.orderNo
          })
        }

        return sorted
      },

      loadEligibility () {
        this.loadingEligibility = true
        this.eligibilityResource.query({ openEnrollmentId: this.openEnrollment.id })
          .then(data => {
            console.log('eligibility')
            console.log(data)
            this.loadingEligibility = false
            this.eligibilities = this.sortEligibilities(data)
          })
          .catch((error) => {
            this.loadingEligibility = false
            this.eligibilities = null
            this.showError(error, 'Failed to load eligibility details!')
          })
      },

      getGroupDetails (oeEligibilityGroupId) {
        let list = null

        if (this.eligibilityGroups && this.eligibilityGroups.length) {
          list = this.eligibilityGroups.filter((group) => {
            return group.oeEligibilityGroupId === oeEligibilityGroupId
          })
        }

        return list
      },

      loadEligibilityGroups () {
        this.loadingEligibilityGroups = true
        this.eligibilityGroupDetailResource.query({ openEnrollmentId: this.openEnrollment.id })
          .then(data => {
            console.log('eligibilityGroup')
            console.log(data)
            this.loadingEligibilityGroups = false
            this.eligibilityGroups = data
          })
          .catch((error) => {
            this.loadingEligibilityGroups = false
            this.eligibilityGroups = null
            this.showError(error, 'Failed to load eligibility group details!')
          })
      },

      showError (error, msg) {
        console.log(error)
        this.errorMessage = (error || msg)
        this.displayError = true
      },
    },
  }
</script>
<style lang="sass">
  .eligibility-summary-group-summary
    border-top: 3px solid rgba(0, 0, 0, 0.1) !important
    border-bottom: 3px solid rgba(0, 0, 0, 0.1) !important
    border-radius: 10px !important
    margin: -3px
    background-color: rgba(199, 203, 199, 0.2)

  .eligibility-summary-group-name
    margin-left: -10px

  .eligibility-summary-group-details
    margin-left: 10px
</style>
